"use client";
import { useContext, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import ButtonGsfurn from "../ButtonGsfurn/ButtonGsfurn";
import MainMenu from "./MainMenu/MainMenu";
import MainMenuExpanded from "./MainMenu/MainMenuExpanded";
import TopMenu from "./TopMenu";
import UspSliderGrinwis from "./UspSliderGrinwis/UspSliderGrinwis";
import SearchForm from "./SearchForm/SearchForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { CartContext } from "context/CartContextProvider";
import { SettingsContext } from "context/SettingsContextProvider";
import { ModalCustomizationWithoutPaddingSmall } from "constants/modal";
import { ModalContext } from "@notanumber/nan-component-library";
import Socials from "./Socials/Socials";
import DealerLoginOverlay from "./DealerLoginOverlay/DealerLoginOverlay";
import { UserContext } from "context/UserContextProvider";

function Logo({ src }) {
  if (!src) return;

  return (
    <a
      href="/"
      title="Home"
      className="block"
    >
      <div className="relative lg:w-40">
        <Image
          src={src}
          alt="Logo"
          width={160}
          height={40}
          className="object-contain w-auto !h-8 lg:!h-10"
          priority
        />
      </div>
    </a>
  );
}

const Header = ({ shrinkHeight }) => {
  const { settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const top_menu = settings?.menus?.top_menu || [];
  const main_menu = settings?.menus?.main_menu || [];
  const usps = settings?.options?.usps || [];
  const email = settings?.options?.email;
  const phone = settings?.options?.telefoonnummer;
  const logo = settings?.logo;

  const { setOverlay } = useContext(ModalContext);
  const { useCartCount } = useContext(CartContext);
  const cartCount = useCartCount() || 0;

  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { contact: user_contact } = user || {};
  const dealerIsLoggedIn = user_contact?.is_dealer;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-100 w-full bg-white divide-y divide-[#F4F3F5]`}
    >
      <div className={`hidden lg:block transition-all duration-700 ${shrinkHeight ? "h-0 opacity-0" : "h-8"}`}>
        <div className="flex items-center justify-center h-full px-4 mx-auto xl:px-0 max-w-app">
          <div className="flex flex-wrap flex-grow min-w-0 text-light-text">
            <UspSliderGrinwis usps={usps} />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setOverlay(
                  <DealerLoginOverlay dealerIsLoggedIn={dealerIsLoggedIn} />,
                  ModalCustomizationWithoutPaddingSmall
                );
              }}
              aria-label="Dealer login"
              className="flex items-center flex-shrink-0 gap-2 font-medium no-underline"
            >
              <FontAwesomeIcon
                icon={faUser}
                className="w-3 text-primary"
              />
              {!dealerIsLoggedIn ? (
                <span className="text-xs font-medium text-dark-text">Dealer login</span>
              ) : (
                <span className="text-xs font-medium text-dark-text">Hi, {user_contact?.company_name || ""}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="px-4 pb-2 mx-auto xl:px-0 max-w-app lg:pb-0">
          <div className="grid grid-cols-12 grid-rows-[auto_auto] lg:grid-rows-[5rem]">
            <div className="flex items-center h-16 col-span-6 row-start-1 lg:col-span-3 lg:h-20">
              <Logo src={logo} />
            </div>

            <div className="flex items-center justify-end h-16 col-span-6 row-start-1 lg:col-span-3 lg:col-start-10 lg:h-20">
              <div className="flex">
                <ButtonGsfurn
                  color="cart_header_btn"
                  size="cart_header_btn"
                  pill
                  onClick={() => router.push("/winkelwagen")}
                >
                  <div className="flex flex-row gap-2 lg:gap-3">
                    <span className="whitespace-nowrap">Offerte aanvragen</span>
                    {cartCount > 0 && (
                      <span
                        className={`font-bold text-white lg:text-dark-text inline-flex h-5 w-5 items-center justify-center rounded-full leading-none bg-primary lg:bg-white text-xs`}
                      >
                        {cartCount}
                      </span>
                    )}
                  </div>
                </ButtonGsfurn>
              </div>
            </div>

            <div className="col-span-12 row-start-2 lg:row-start-1 lg:col-span-6 lg:col-start-4 lg:flex lg:items-center lg:h-20">
              <hr className="mb-2 -mx-4 lg:hidden" />

              <div className="w-full">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-lg lg:hidden h-11 w-11 bg-footer"
                    aria-label="Open menu"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    <span className={`relative block w-5 h-4 ${mobileMenuOpen ? "!-mb-1" : ""}`}>
                      <span className={`absolute left-0 top-0 block h-[0.125rem] w-full bg-white transition-all ${mobileMenuOpen ? "rotate-45 !top-[0.3125rem]" : ""}`} />
                      <span className={`absolute left-0 top-1/2 -translate-y-1/2 block h-[0.125rem] w-full bg-white ${mobileMenuOpen ? "w-0 opacity-0" : ""}`} />
                      <span className={`absolute left-0 bottom-0 block h-[0.125rem] w-full bg-white ${mobileMenuOpen ? "-rotate-45 !top-[0.3125rem]" : ""}`} />
                    </span>
                  </button>

                  <div className="flex justify-center flex-1">
                    <SearchForm />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop menu */}
      <div className="hidden h-12 lg:block">
        <div className="relative flex items-center justify-between h-full px-4 mx-auto xl:px-0 max-w-app">
          <div className="flex items-center h-full gap-4 text-sm">
            <MainMenuExpanded main_menu={main_menu} shrinkHeight={shrinkHeight} />
            {top_menu?.length > 0 && <TopMenu top_menu={top_menu} />}
          </div>

          <Socials email={email} phone={phone} />
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <MainMenu
          main_menu={main_menu}
          top_menu={top_menu}
          settings={settings}
          setShowMenuExpanded={setMobileMenuOpen}
        />
      )}
    </header>
  );
};

export default Header;
